/**
 * School Periods Service
 *
 * Manages school-specific time periods used for:
 * - Discipline dashboard visualization
 * - Rule evaluation time windows
 * - Class scheduling
 *
 * Collection: schools/{schoolId}/schoolPeriods
 */

import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

// Cache for school periods
let cachedPeriods = null;
let cacheSchoolId = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Default school periods (fallback when no custom periods exist)
const DEFAULT_PERIODS = [
  { id: 'before-school', label: 'Before School', startTime: '00:00', endTime: '07:59', type: 'non-class', color: '#94A3B8', order: 0 },
  { id: 'period-1', label: 'Period 1', startTime: '08:00', endTime: '08:59', type: 'class', color: '#3B82F6', order: 1 },
  { id: 'period-2', label: 'Period 2', startTime: '09:00', endTime: '09:59', type: 'class', color: '#10B981', order: 2 },
  { id: 'period-3', label: 'Period 3', startTime: '10:00', endTime: '10:59', type: 'class', color: '#F59E0B', order: 3 },
  { id: 'break-1', label: 'Morning Break', startTime: '11:00', endTime: '11:14', type: 'break', color: '#6B7280', order: 4 },
  { id: 'period-4', label: 'Period 4', startTime: '11:15', endTime: '12:14', type: 'class', color: '#8B5CF6', order: 5 },
  { id: 'lunch', label: 'Lunch', startTime: '12:15', endTime: '12:59', type: 'break', color: '#6B7280', order: 6 },
  { id: 'period-5', label: 'Period 5', startTime: '13:00', endTime: '13:59', type: 'class', color: '#EC4899', order: 7 },
  { id: 'period-6', label: 'Period 6', startTime: '14:00', endTime: '14:59', type: 'class', color: '#14B8A6', order: 8 },
  { id: 'after-school', label: 'After School', startTime: '15:00', endTime: '23:59', type: 'non-class', color: '#94A3B8', order: 9 }
];

class SchoolPeriodsService {
  constructor() {
    this.collectionName = 'schoolPeriods';
    this.listeners = new Map();
  }

  /**
   * Get collection reference for a specific school
   */
  getCollectionRef(schoolId) {
    if (!schoolId) {
      throw new Error('School ID is required for school periods operations');
    }
    return collection(db, 'schools', schoolId, this.collectionName);
  }

  /**
   * Clear cache when school changes
   */
  clearCache() {
    cachedPeriods = null;
    cacheSchoolId = null;
    cacheTimestamp = null;
  }

  /**
   * Get all school periods for a school (with caching)
   */
  async getAll(schoolId, { allowDefaults = true } = {}) {
    if (!schoolId) {
      console.warn('No schoolId provided, returning default periods');
      return allowDefaults ? DEFAULT_PERIODS : [];
    }

    // Check cache
    if (cachedPeriods &&
        cacheSchoolId === schoolId &&
        cacheTimestamp &&
        Date.now() - cacheTimestamp < CACHE_DURATION) {
      return cachedPeriods;
    }

    try {
      const collectionRef = this.getCollectionRef(schoolId);
      const q = query(collectionRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        // No custom periods, return defaults
        return allowDefaults ? DEFAULT_PERIODS : [];
      }

      const periods = [];
      snapshot.forEach((doc) => {
        periods.push({ id: doc.id, ...doc.data() });
      });

      // Update cache
      cachedPeriods = periods;
      cacheSchoolId = schoolId;
      cacheTimestamp = Date.now();

      return periods;
    } catch (error) {
      console.error('Error fetching school periods:', error);
      return allowDefaults ? DEFAULT_PERIODS : [];
    }
  }

  /**
   * Subscribe to real-time updates
   */
  subscribe(schoolId, callback) {
    if (!schoolId) {
      console.warn('No schoolId provided for subscription');
      callback(DEFAULT_PERIODS);
      return () => {};
    }

    // Unsubscribe from previous listener if exists
    this.unsubscribe(schoolId);

    const collectionRef = this.getCollectionRef(schoolId);
    const q = query(collectionRef, orderBy('order', 'asc'));

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        if (snapshot.empty) {
          callback(DEFAULT_PERIODS);
          return;
        }

        const periods = [];
        snapshot.forEach((doc) => {
          periods.push({ id: doc.id, ...doc.data() });
        });

        // Update cache
        cachedPeriods = periods;
        cacheSchoolId = schoolId;
        cacheTimestamp = Date.now();

        callback(periods);
      },
      (error) => {
        console.error('Error in school periods subscription:', error);
        callback(DEFAULT_PERIODS);
      }
    );

    this.listeners.set(schoolId, unsubscribe);
    return unsubscribe;
  }

  /**
   * Unsubscribe from real-time updates
   */
  unsubscribe(schoolId) {
    const unsubscribe = this.listeners.get(schoolId);
    if (unsubscribe) {
      unsubscribe();
      this.listeners.delete(schoolId);
    }
  }

  /**
   * Create or update a school period
   */
  async save(schoolId, periodData) {
    if (!schoolId) {
      throw new Error('School ID is required');
    }

    const { id, ...data } = periodData;
    const periodId = id || this.generatePeriodId(data.label);

    // Add timestamps
    const now = new Date();
    if (!id) {
      data.createdAt = now;
    }
    data.updatedAt = now;

    const docRef = doc(this.getCollectionRef(schoolId), periodId);
    await setDoc(docRef, data, { merge: true });

    // Clear cache
    this.clearCache();

    return { id: periodId, ...data };
  }

  /**
   * Delete a school period
   */
  async delete(schoolId, periodId) {
    if (!schoolId || !periodId) {
      throw new Error('School ID and Period ID are required');
    }

    const docRef = doc(this.getCollectionRef(schoolId), periodId);
    await deleteDoc(docRef);

    // Clear cache
    this.clearCache();
  }

  /**
   * Bulk update periods (replace all)
   */
  async bulkUpdate(schoolId, periods) {
    if (!schoolId) {
      throw new Error('School ID is required');
    }

    const batch = writeBatch(db);
    const collectionRef = this.getCollectionRef(schoolId);

    // Delete existing periods
    const existingSnapshot = await getDocs(collectionRef);
    existingSnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });

    // Add new periods
    const now = new Date();
    periods.forEach((period, index) => {
      const periodId = period.id || this.generatePeriodId(period.label);
      const docRef = doc(collectionRef, periodId);
      batch.set(docRef, {
        ...period,
        order: period.order ?? index,
        createdAt: now,
        updatedAt: now
      });
    });

    await batch.commit();

    // Clear cache
    this.clearCache();
  }

  /**
   * Reset to default periods
   */
  async resetToDefaults(schoolId) {
    return this.bulkUpdate(schoolId, DEFAULT_PERIODS);
  }

  /**
   * Get current period based on time
   */
  getCurrentPeriod(periods, time = new Date()) {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    const currentMinutes = hours * 60 + minutes;

    for (const period of periods) {
      const [startHour, startMinute] = period.startTime.split(':').map(Number);
      const [endHour, endMinute] = period.endTime.split(':').map(Number);

      const startMinutes = startHour * 60 + startMinute;
      const endMinutes = endHour * 60 + endMinute;

      if (currentMinutes >= startMinutes && currentMinutes <= endMinutes) {
        return period;
      }
    }

    // Default to after school if no period matches
    return periods.find(p => p.id === 'after-school') || periods[periods.length - 1];
  }

  /**
   * Validate period data
   */
  validatePeriod(period) {
    const errors = [];

    if (!period.label) {
      errors.push('Period label is required');
    }

    if (!period.startTime || !this.isValidTime(period.startTime)) {
      errors.push('Valid start time (HH:MM) is required');
    }

    if (!period.endTime || !this.isValidTime(period.endTime)) {
      errors.push('Valid end time (HH:MM) is required');
    }

    if (period.startTime && period.endTime) {
      const [startHour, startMinute] = period.startTime.split(':').map(Number);
      const [endHour, endMinute] = period.endTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMinute;
      const endMinutes = endHour * 60 + endMinute;

      if (startMinutes >= endMinutes) {
        errors.push('End time must be after start time');
      }
    }

    if (!period.type || !['class', 'break', 'non-class'].includes(period.type)) {
      errors.push('Period type must be class, break, or non-class');
    }

    if (!period.color || !this.isValidColor(period.color)) {
      errors.push('Valid color is required');
    }

    return errors;
  }

  /**
   * Check for period overlaps
   */
  checkOverlaps(periods) {
    const overlaps = [];

    for (let i = 0; i < periods.length; i++) {
      for (let j = i + 1; j < periods.length; j++) {
        const period1 = periods[i];
        const period2 = periods[j];

        const [start1Hour, start1Minute] = period1.startTime.split(':').map(Number);
        const [end1Hour, end1Minute] = period1.endTime.split(':').map(Number);
        const [start2Hour, start2Minute] = period2.startTime.split(':').map(Number);
        const [end2Hour, end2Minute] = period2.endTime.split(':').map(Number);

        const start1 = start1Hour * 60 + start1Minute;
        const end1 = end1Hour * 60 + end1Minute;
        const start2 = start2Hour * 60 + start2Minute;
        const end2 = end2Hour * 60 + end2Minute;

        // Check for overlap
        if ((start1 < end2 && end1 > start2) || (start2 < end1 && end2 > start1)) {
          overlaps.push({
            period1: period1.label,
            period2: period2.label,
            message: `${period1.label} (${period1.startTime}-${period1.endTime}) overlaps with ${period2.label} (${period2.startTime}-${period2.endTime})`
          });
        }
      }
    }

    return overlaps;
  }

  /**
   * Helper: Generate period ID from label
   */
  generatePeriodId(label) {
    return label.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  /**
   * Helper: Validate time format
   */
  isValidTime(time) {
    const regex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return regex.test(time);
  }

  /**
   * Helper: Validate color format
   */
  isValidColor(color) {
    const regex = /^#[0-9A-Fa-f]{6}$/;
    return regex.test(color);
  }

  /**
   * Get default periods (for reference)
   */
  getDefaultPeriods() {
    return DEFAULT_PERIODS;
  }
}

// Export singleton instance
export const schoolPeriodsService = new SchoolPeriodsService();
